// Tool 3: V-Mart analytics — přehled GA4 dat.
// Fáze 1: Přehled tab (KPIs + Funnel + Sources). Ostatní taby placeholderem.

import { CartRateSummary } from '@/components/analytics/CartRateSummary';
import { CartRateTab } from '@/components/analytics/CartRateTab';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import type { MeResponse } from '@sklad-sync/shared';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

// 10 barev pro poznámky (vybrané pro dobré odlišení na grafu).
const NOTE_COLORS: Array<{ hex: string; name: string }> = [
  { hex: '#ef4444', name: 'Červená' },
  { hex: '#f97316', name: 'Oranžová' },
  { hex: '#eab308', name: 'Žlutá' },
  { hex: '#22c55e', name: 'Zelená' },
  { hex: '#14b8a6', name: 'Tyrkysová' },
  { hex: '#3b82f6', name: 'Modrá' },
  { hex: '#6366f1', name: 'Indigová' },
  { hex: '#a855f7', name: 'Fialová' },
  { hex: '#ec4899', name: 'Růžová' },
  { hex: '#6b7280', name: 'Šedá' },
];
const DEFAULT_LINE_COLOR = '#3b82f6'; // tailwind primary blue

type AnalyticsNote = {
  id: number;
  note_date: string;
  note: string;
  color: string;
  created_at: number;
  created_by: string | null;
  scope: string;
};

type Props = { user: MeResponse };

type Range = { startDate: string; endDate: string };

type OverviewKpis = {
  sessions: number;
  purchases: number;
  revenue: number;
  cr: number;
  aov: number;
};

type FunnelData = {
  visitors: number;
  view_item: number;
  add_to_cart: number;
  cart: number;
  shipping: number;
  customer: number;
  purchase: number;
};

type SourceRow = {
  source: string;
  medium: string;
  campaign: string;
  sessions: number;
  purchases: number;
  revenue: number;
  cr: number;
};

type DeviceRow = {
  category: string;
  label: string;
  visitors: number;
  sessions: number;
  purchases: number;
  revenue: number;
  cr: number;
  purchaseShare: number;
};

type OverviewResponse = {
  range: Range;
  kpis: OverviewKpis;
  funnel: FunnelData;
  sources: SourceRow[];
  devices: DeviceRow[];
};

type TrendMetric =
  | 'sessions'
  | 'visitors'
  | 'transactions'
  | 'revenue'
  | 'cr'
  | 'aov'
  | 'event'
  | 'pageview'
  | 'funnel_pct'
  | 'visitor_to_cart_pct';
type TrendUnit = 'count' | 'currency' | 'percent';

type TrendResponse = {
  metric: TrendMetric;
  label: string;
  unit: TrendUnit;
  range: Range;
  series: Array<{ date: string; value: number }>;
};

type TrendQuery = {
  metric: TrendMetric;
  eventName?: string;
  pagePath?: string;
  step?: string;
  label: string;
};

type Preset = '7d' | '30d' | '90d';

function presetRange(preset: Preset): Range {
  const now = new Date();
  const end = now.toISOString().slice(0, 10);
  const startDate = new Date(now);
  const days = preset === '7d' ? 6 : preset === '30d' ? 29 : 89;
  startDate.setUTCDate(startDate.getUTCDate() - days);
  return { startDate: startDate.toISOString().slice(0, 10), endDate: end };
}

function formatCzk(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatNum(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 }).format(n);
}

function formatPct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function formatDateCs(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}. ${m}. ${y}`;
}

function formatDateShort(iso: string): string {
  // "2026-05-14" → "14.5."
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(m) || !Number.isFinite(d)) return iso;
  return `${d}.${m}.`;
}

export function AnalyticsPage(_props: Props) {
  const [preset, setPreset] = useState<Preset | null>('30d');
  const initial = presetRange('30d');
  const [fromDate, setFromDate] = useState<string>(initial.startDate);
  const [toDate, setToDate] = useState<string>(initial.endDate);
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trendQuery, setTrendQuery] = useState<TrendQuery | null>(null);
  const [showFunnelInfo, setShowFunnelInfo] = useState(false);
  const [showCartConvInfo, setShowCartConvInfo] = useState(false);
  const [notes, setNotes] = useState<AnalyticsNote[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'products' | 'kosik' | 'ux' | 'services'>(
    'overview',
  );
  // Tab Produkty čte data ze Shoptet objednávek — ty máme od 11.10.2025.
  // Dřívější datum v tomhle tabu nedává smysl, tak ho v pickeru zakážeme.
  const [ordersMinDate, setOrdersMinDate] = useState<string | null>(null);

  const loadNotes = useCallback(async () => {
    try {
      const r = await apiFetch<{ notes: AnalyticsNote[] }>('/api/analytics/notes');
      setNotes(r.notes);
    } catch (_e) {
      // notes nejsou kritické pro stránku
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // Zjisti nejstarší objednávku v DB → minimum pro date picker v tabu Produkty.
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch<{ oldest: string | null }>('/api/orders/status');
        if (r.oldest) setOrdersMinDate(r.oldest.slice(0, 10));
      } catch (_e) {
        // nekritické — bez limitu picker funguje dál
      }
    })();
  }, []);

  // V tabu Produkty zarovnej "Od" nahoru, kdyby bylo před začátkem objednávek.
  useEffect(() => {
    if (activeTab === 'products' && ordersMinDate && fromDate < ordersMinDate) {
      setFromDate(ordersMinDate);
      setPreset(null);
    }
  }, [activeTab, ordersMinDate, fromDate]);

  const range: Range = { startDate: fromDate, endDate: toDate };

  function selectPreset(p: Preset) {
    setPreset(p);
    const r = presetRange(p);
    setFromDate(r.startDate);
    setToDate(r.endDate);
  }

  function onFromChange(v: string) {
    let next = v;
    if (activeTab === 'products' && ordersMinDate && v && v < ordersMinDate) {
      next = ordersMinDate; // tab Produkty: nedovol datum před začátkem objednávek
    }
    setFromDate(next);
    setPreset(null); // přepnuli jsme na custom
  }
  function onToChange(v: string) {
    setToDate(v);
    setPreset(null);
  }

  const load = useCallback(async () => {
    if (!fromDate || !toDate) return;
    if (fromDate > toDate) {
      setError('"Od" datum nesmí být po "Do".');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ from: fromDate, to: toDate });
      const res = await apiFetch<OverviewResponse>(`/api/analytics/overview?${params.toString()}`);
      setData(res);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.describe() : 'Chyba načítání');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      {/* Header s date pickerem */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">V-Mart Analytics</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            {formatDateCs(range.startDate)} — {formatDateCs(range.endDate)}
            {activeTab === 'products' && ordersMinDate && (
              <span className="text-text-muted">
                {' '}
                · objednávky od {formatDateCs(ordersMinDate)}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Custom date picker */}
          <div className="flex items-center gap-1 bg-surface border border-border rounded-lg px-2 py-1.5">
            <input
              type="date"
              value={fromDate}
              min={activeTab === 'products' && ordersMinDate ? ordersMinDate : undefined}
              max={toDate}
              onChange={(e) => onFromChange(e.target.value)}
              className="text-sm text-text bg-transparent outline-none cursor-pointer"
              aria-label="Od"
            />
            <span className="text-text-muted">—</span>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => onToChange(e.target.value)}
              className="text-sm text-text bg-transparent outline-none cursor-pointer"
              aria-label="Do"
            />
          </div>
          {/* Preset buttons */}
          <div className="flex bg-surface border border-border rounded-lg overflow-hidden">
            {(['7d', '30d', '90d'] as Preset[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => selectPreset(p)}
                className={`text-sm font-medium px-3 py-2 cursor-pointer transition-colors ${
                  preset === p ? 'bg-primary text-white' : 'text-text-secondary hover:bg-bg'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-sm font-medium text-text-secondary hover:text-text bg-bg hover:bg-surface-hover border border-border rounded-lg px-3 py-2 cursor-pointer transition-colors disabled:opacity-50"
          >
            {loading ? 'Načítám…' : 'Obnovit'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border flex gap-1">
        {(
          [
            { key: 'overview' as const, label: 'Přehled', enabled: true },
            { key: 'products' as const, label: 'Produkty', enabled: true },
            { key: 'kosik' as const, label: 'Do košíku', enabled: true },
            { key: 'ux' as const, label: 'UX úpravy', enabled: true },
            { key: 'services' as const, label: 'Služby', enabled: true },
            { key: 'sources' as const, label: 'Zdroje', enabled: false },
            { key: 'time' as const, label: 'Čas', enabled: false },
            { key: 'geo' as const, label: 'Geo', enabled: false },
            { key: 'conversions' as const, label: 'Konverze', enabled: false },
          ] as const
        ).map((t) => {
          const isActive = activeTab === t.key;
          if (!t.enabled) {
            return (
              <div
                key={t.key}
                className="text-sm font-medium px-4 py-2 text-text-muted opacity-60 cursor-not-allowed"
                title="Připravujeme v další fázi"
              >
                {t.label}
              </div>
            );
          }
          return (
            <button
              key={t.key}
              type="button"
              onClick={() =>
                setActiveTab(t.key as 'overview' | 'products' | 'kosik' | 'ux' | 'services')
              }
              className={`text-sm font-medium px-4 py-2 cursor-pointer transition-colors ${
                isActive
                  ? 'text-primary border-b-2 border-primary -mb-px'
                  : 'text-text-secondary hover:text-text'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {activeTab === 'products' && <ProductsTab range={range} />}

      {activeTab === 'kosik' && (
        <>
          <CartRateSummary range={range} />
          <CartRateTab range={range} />
        </>
      )}

      {activeTab === 'ux' && <UxEvalTab range={range} />}

      {activeTab === 'services' && <ServicesTab />}

      {activeTab === 'overview' && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Relace"
              ga4Ref="sessions"
              value={data ? formatNum(data.kpis.sessions) : '—'}
              loading={loading}
              onClick={() => setTrendQuery({ metric: 'sessions', label: 'Relace' })}
            />
            <KpiCard
              label="Nákupy"
              ga4Ref="transactions"
              value={data ? formatNum(data.kpis.purchases) : '—'}
              loading={loading}
              onClick={() => setTrendQuery({ metric: 'transactions', label: 'Nákupy' })}
            />
            <KpiCard
              label="Konverzní poměr"
              ga4Ref="transactions / sessions"
              value={data ? formatPct(data.kpis.cr) : '—'}
              loading={loading}
              onClick={() => setTrendQuery({ metric: 'cr', label: 'Konverzní poměr' })}
            />
            <KpiCard
              label="Prům. objednávka"
              ga4Ref="purchaseRevenue / transactions"
              value={data ? formatCzk(data.kpis.aov) : '—'}
              loading={loading}
              onClick={() => setTrendQuery({ metric: 'aov', label: 'Prům. objednávka' })}
            />
          </div>

          {/* Horní statistiky — návštěvníci + zobrazení produktu */}
          {data && (
            <div className="bg-surface rounded-xl p-5 shadow-card mb-4 space-y-3">
              <FunnelStatRow
                label="Celkem návštěvníků"
                ga4Ref="totalUsers"
                value={data.funnel.visitors}
                onClick={() => setTrendQuery({ metric: 'visitors', label: 'Návštěvníci' })}
              />
              <FunnelStatRow
                label="Zobrazení produktu"
                ga4Ref="event: view_item"
                value={data.funnel.view_item}
                subRight={
                  data.funnel.visitors > 0
                    ? `${((data.funnel.view_item / data.funnel.visitors) * 100).toFixed(1)}% z návštěvníků`
                    : undefined
                }
                onClick={() =>
                  setTrendQuery({
                    metric: 'event',
                    eventName: 'view_item',
                    label: 'Zobrazení produktu',
                  })
                }
              />
            </div>
          )}

          {/* Návštěvník → košík (add_to_cart) — konverze před funnelem */}
          {data && (
            <div className="bg-surface rounded-xl p-5 shadow-card mb-4">
              <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
                <h2 className="text-base font-bold text-text">Návštěvník → košík (add_to_cart)</h2>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCartConvInfo((v) => !v)}
                    className="text-xs font-medium text-primary hover:text-primary-dark cursor-pointer"
                  >
                    {showCartConvInfo ? '× Skrýt' : '? Jak se to počítá'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTrendQuery({
                        metric: 'visitor_to_cart_pct',
                        label: 'Konverze návštěvník → add_to_cart',
                      })
                    }
                    className="text-xs font-medium text-primary hover:text-primary-dark cursor-pointer"
                  >
                    Trend konverze →
                  </button>
                </div>
              </div>
              {showCartConvInfo && <CartConvInfo />}
              <div className="space-y-3">
                <FunnelStatRow
                  label="Návštěvníci"
                  ga4Ref="totalUsers"
                  value={data.funnel.visitors}
                  onClick={() => setTrendQuery({ metric: 'visitors', label: 'Návštěvníci' })}
                />
                <FunnelStatRow
                  label="Přidání do košíku (klik)"
                  ga4Ref="event: add_to_cart"
                  value={data.funnel.add_to_cart}
                  subRight={
                    data.funnel.visitors > 0
                      ? `${((data.funnel.add_to_cart / data.funnel.visitors) * 100).toFixed(2)}% z návštěvníků`
                      : undefined
                  }
                  onClick={() =>
                    setTrendQuery({
                      metric: 'event',
                      eventName: 'add_to_cart',
                      label: 'Přidání do košíku (event)',
                    })
                  }
                />
              </div>
            </div>
          )}

          {/* Funnel */}
          <div className="bg-surface rounded-xl p-5 shadow-card mb-6">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-base font-bold text-text">Nákupní funnel</h2>
              <button
                type="button"
                onClick={() => setShowFunnelInfo((v) => !v)}
                className="text-xs font-medium text-primary hover:text-primary-dark cursor-pointer"
              >
                {showFunnelInfo ? '× Skrýt' : '? Jak se to počítá'}
              </button>
            </div>
            {showFunnelInfo && <FunnelInfo />}
            {data && <Funnel data={data.funnel} onStepClick={(q) => setTrendQuery(q)} />}
            {!data && !loading && (
              <div className="text-sm text-text-muted">Žádná data k zobrazení.</div>
            )}
          </div>

          {/* Zařízení — kolik lidí nakupuje z mobilu/tabletu/počítače */}
          <div className="bg-surface rounded-xl shadow-card">
            <h2 className="text-base font-bold text-text px-5 pt-5 pb-2 overflow-x-auto">
              Nákupy podle zařízení
            </h2>
            <table className="w-full text-sm">
              <thead className="bg-bg border-b border-border text-text-secondary">
                <tr>
                  <th className="text-left font-semibold px-4 py-2.5">Zařízení</th>
                  <th className="text-right font-semibold px-4 py-2.5">Nákupy</th>
                  <th className="text-left font-semibold px-4 py-2.5 w-2/5">Podíl nákupů</th>
                  <th className="text-right font-semibold px-4 py-2.5">Návštěvníci</th>
                  <th className="text-right font-semibold px-4 py-2.5">Tržby (Kč)</th>
                  <th className="text-right font-semibold px-4 py-2.5 w-20">CR%</th>
                </tr>
              </thead>
              <tbody>
                {data?.devices.map((d) => (
                  <tr
                    key={d.category}
                    className="border-b border-border last:border-0 hover:bg-bg/50"
                  >
                    <td className="px-4 py-2.5 text-text font-medium">{d.label}</td>
                    <td className="px-4 py-2.5 text-right text-text">{formatNum(d.purchases)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-bg rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: `${Math.min(d.purchaseShare, 100)}%` }}
                          />
                        </div>
                        <span className="text-text-secondary w-12 text-right">
                          {formatPct(d.purchaseShare)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right text-text-secondary">
                      {formatNum(d.visitors)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-text-secondary">
                      {formatNum(d.revenue)}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <CrBadge cr={d.cr} />
                    </td>
                  </tr>
                ))}
                {!loading && (!data || data.devices.length === 0) && (
                  <tr>
                    <td colSpan={6} className="text-center text-text-muted py-8">
                      Žádná data o zařízeních za vybrané období.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Sources */}
          <div className="bg-surface rounded-xl shadow-card">
            <h2 className="text-base font-bold text-text px-5 pt-5 pb-2 overflow-x-auto">
              Zdroje návštěvnosti
            </h2>
            <table className="w-full text-sm">
              <thead className="bg-bg border-b border-border text-text-secondary">
                <tr>
                  <th className="text-left font-semibold px-4 py-2.5">Zdroj</th>
                  <th className="text-left font-semibold px-4 py-2.5">Médium</th>
                  <th className="text-left font-semibold px-4 py-2.5">Kampaň</th>
                  <th className="text-right font-semibold px-4 py-2.5">Relace</th>
                  <th className="text-right font-semibold px-4 py-2.5">Nákupy</th>
                  <th className="text-right font-semibold px-4 py-2.5">Tržby (Kč)</th>
                  <th className="text-right font-semibold px-4 py-2.5 w-20">CR%</th>
                </tr>
              </thead>
              <tbody>
                {data?.sources.map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-bg/50">
                    <td className="px-4 py-2.5 text-text">{r.source || '(direct)'}</td>
                    <td className="px-4 py-2.5 text-text-secondary">{r.medium || '(none)'}</td>
                    <td className="px-4 py-2.5 text-text-secondary">{r.campaign || '(not set)'}</td>
                    <td className="px-4 py-2.5 text-right">{formatNum(r.sessions)}</td>
                    <td className="px-4 py-2.5 text-right">{formatNum(r.purchases)}</td>
                    <td className="px-4 py-2.5 text-right">{formatNum(r.revenue)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <CrBadge cr={r.cr} />
                    </td>
                  </tr>
                ))}
                {!loading && (!data || data.sources.length === 0) && (
                  <tr>
                    <td colSpan={7} className="text-center text-text-muted py-8">
                      Žádné zdroje za vybrané období.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Trend modal (dostupný napříč všemi taby) */}
      {trendQuery && (
        <TrendModal
          query={trendQuery}
          range={range}
          notes={notes}
          onNotesChanged={loadNotes}
          onClose={() => setTrendQuery(null)}
        />
      )}
    </>
  );
}

// ─── UX úpravy tab — vyhodnocování dopadu jednotlivých UX změn ────────────

type StickyStats = {
  range: { from: string; to: string };
  kusy: number;
  hodnota: number;
  products: Array<{
    product_code: string;
    product_name: string | null;
    clicks: number;
    value: number;
  }>;
};

function UxEvalTab({ range }: { range: Range }) {
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-text">Vyhodnocování UX úprav</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          Měření dopadu jednotlivých UX změn na e-shopu.
        </p>
      </div>

      <div className="space-y-5">
        <StickyExperimentCard
          range={range}
          platform="pc"
          title={'PC — sticky button „Přidat do košíku“'}
          description="Přidání do košíku přes sticky lištu při scrollu (jen desktop). Počítá se klik s vybranou velikostí."
        />
        <StickyExperimentCard
          range={range}
          platform="mobile"
          title={'Mobil — sticky lišta „Přidat do košíku“'}
          description="Mini widget dole na obrazovce při scrollu (náhled, velikost, cena, tlačítko košíku)."
        />
      </div>
    </div>
  );
}

function StickyExperimentCard({
  range,
  platform,
  title,
  description,
}: {
  range: Range;
  platform: 'pc' | 'mobile';
  title: string;
  description: string;
}) {
  const [data, setData] = useState<StickyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          from: range.startDate,
          to: range.endDate,
          platform,
        });
        const res = await apiFetch<StickyStats>(`/api/sticky-cart/stats?${params.toString()}`);
        if (!cancelled) setData(res);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.describe() : 'Chyba načítání');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.startDate, range.endDate, platform]);

  return (
    <div className="bg-surface rounded-xl shadow-card p-5">
      <div className="mb-4">
        <h3 className="font-semibold text-text">{title}</h3>
        <p className="text-xs text-text-muted mt-0.5">{description}</p>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-5">
        <KpiCard label="Přidáno kusů" value={data ? formatNum(data.kusy) : '—'} loading={loading} />
        <KpiCard
          label="Hodnota v košíku"
          value={data ? formatCzk(data.hodnota) : '—'}
          loading={loading}
        />
      </div>

      {data && data.products.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-muted border-b border-border">
                <th className="font-medium py-2 pr-3">Produkt</th>
                <th className="font-medium py-2 px-3 text-right">Kusů</th>
                <th className="font-medium py-2 pl-3 text-right">Hodnota</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((p) => (
                <tr key={p.product_code} className="border-b border-border/50">
                  <td className="py-2 pr-3">
                    <div className="text-text">{p.product_name || p.product_code}</div>
                    <div className="text-xs text-text-muted font-mono">{p.product_code}</div>
                  </td>
                  <td className="py-2 px-3 text-right text-text">{formatNum(p.clicks)}</td>
                  <td className="py-2 pl-3 text-right text-text">{formatCzk(p.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !loading && (
          <div className="text-sm text-text-muted py-6 text-center">
            Za vybrané období zatím žádná data.
          </div>
        )
      )}
    </div>
  );
}

// ─── Služby tab — podíl objednávek s doplňkovou službou, po měsících ─────

type ServiceMonth = {
  ym: string;
  total_orders: number;
  with_service: number;
  pct: number;
  revenue_without_vat: number;
};
type ServicesResponse = { service_name: string; from: string; months: ServiceMonth[] };

/** 'YYYY-MM' → 'led 26' (krátký český popisek měsíce pro osu grafu). */
function formatMonthShort(ym: string): string {
  const [y, m] = ym.split('-');
  const idx = Number(m) - 1;
  const names = [
    'led',
    'úno',
    'bře',
    'dub',
    'kvě',
    'čvn',
    'čvc',
    'srp',
    'zář',
    'říj',
    'lis',
    'pro',
  ];
  const label = names[idx] ?? m;
  return `${label} ${(y ?? '').slice(2)}`;
}

function ServicesTab() {
  const [notes, setNotes] = useState<AnalyticsNote[]>([]);
  // Rozsah pro NotesPanel je pevně od 1.1.2026, stejně jako oba grafy níž.
  const noteRange: Range = {
    startDate: '2026-01-01',
    endDate: new Date().toISOString().slice(0, 10),
  };

  const loadNotes = useCallback(async () => {
    try {
      const r = await apiFetch<{ notes: AnalyticsNote[] }>('/api/analytics/notes?scope=services');
      setNotes(r.notes);
    } catch {
      /* poznámky nejsou kritické pro zobrazení grafů */
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold text-text">Služby</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          Podíl objednávek s doplňkovou službou, po měsících.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ServiceMetricCard
            endpoint="/api/analytics/services/prednostni-expedice"
            title={'Podíl objednávek s „Přednostní expedicí"'}
            description={
              '% ze všech nestornovaných objednávek CZ e-shopu v daném měsíci, které obsahovaly ' +
              'položku „Přednostní expedice". Chystá se zlevnění služby — sledujeme, jestli se ' +
              'podíl pohne.'
            }
            columnLabel="S expedicí"
            notes={notes}
          />
          <ServiceMetricCard
            endpoint="/api/analytics/services/dysko"
            title={'Podíl objednávek s „Dýškem švadlenkám"'}
            description={
              '% ze všech nestornovaných objednávek CZ e-shopu v daném měsíci, které obsahovaly ' +
              'jakékoliv dýško švadlenkám (libovolná z nabízených částek — 20/30/50/100/200 Kč).'
            }
            columnLabel="S dýškem"
            notes={notes}
          />
        </div>
        <NotesPanel notes={notes} range={noteRange} onChanged={loadNotes} scope="services" />
      </div>
    </div>
  );
}

function ServiceMetricCard({
  endpoint,
  title,
  description,
  columnLabel,
  notes,
}: {
  endpoint: string;
  title: string;
  description: string;
  columnLabel: string;
  notes: AnalyticsNote[];
}) {
  const [data, setData] = useState<ServicesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiFetch<ServicesResponse>(endpoint);
        if (!cancelled) setData(res);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.describe() : 'Chyba načítání');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [endpoint]);

  return (
    <div className="bg-surface rounded-xl shadow-card p-5">
      <div className="mb-4">
        <h3 className="font-semibold text-text">{title}</h3>
        <p className="text-xs text-text-muted mt-0.5">{description}</p>
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-text-muted py-12 text-center">Načítám…</div>
      ) : data && data.months.length > 0 ? (
        <>
          <ServicesBarChart months={data.months} notes={notes} />
          <ServicesMonthTable months={data.months} columnLabel={columnLabel} />
        </>
      ) : (
        !error && <div className="text-sm text-text-muted py-12 text-center">Žádná data.</div>
      )}
    </div>
  );
}

function ServicesMonthTable({
  months,
  columnLabel,
}: {
  months: ServiceMonth[];
  columnLabel: string;
}) {
  return (
    <div className="overflow-x-auto mt-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-text-muted border-b border-border">
            <th className="font-medium py-2 pr-3">Měsíc</th>
            <th className="font-medium py-2 px-3 text-right">Objednávek</th>
            <th className="font-medium py-2 px-3 text-right">{columnLabel}</th>
            <th className="font-medium py-2 px-3 text-right">Podíl</th>
            <th className="font-medium py-2 pl-3 text-right">Tržba bez DPH</th>
          </tr>
        </thead>
        <tbody>
          {[...months].reverse().map((m) => (
            <tr key={m.ym} className="border-b border-border/50">
              <td className="py-2 pr-3 text-text">{formatMonthShort(m.ym)}</td>
              <td className="py-2 px-3 text-right text-text">{formatNum(m.total_orders)}</td>
              <td className="py-2 px-3 text-right text-text">{formatNum(m.with_service)}</td>
              <td className="py-2 px-3 text-right text-text">{m.pct.toFixed(1)}%</td>
              <td className="py-2 pl-3 text-right text-text">{formatCzk(m.revenue_without_vat)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Barva sloupce podle poznámek s granularitou měsíce (ne dne jako
 * `colorForDate`) — poznámka ze 4. 9. tak obarví rovnou celý zářijový
 * sloupec, i když formálně platí až od toho dne. U měsíčního grafu je to
 * čitelnější než dělit sloupec napůl.
 */
function colorForYm(ym: string, sortedNotes: AnalyticsNote[]): string {
  let active = DEFAULT_LINE_COLOR;
  for (const n of sortedNotes) {
    if (n.note_date.slice(0, 7) <= ym) active = n.color;
    else break;
  }
  return active;
}

function ServicesBarChart({
  months,
  notes = [],
}: {
  months: ServiceMonth[];
  notes?: AnalyticsNote[];
}) {
  const W = 700;
  const H = 280;
  const PAD = { l: 40, r: 16, t: 16, b: 40 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const maxPct = Math.max(...months.map((m) => m.pct), 10);
  const barGap = 8;
  const barW = months.length > 0 ? innerW / months.length - barGap : 0;

  function barX(i: number) {
    return PAD.l + i * (innerW / months.length);
  }
  function barY(pct: number) {
    return PAD.t + innerH - (pct / maxPct) * innerH;
  }

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * maxPct);
  const hovered = hoverIdx !== null ? months[hoverIdx] : null;
  const sortedNotes = [...notes].sort((a, b) => (a.note_date < b.note_date ? -1 : 1));
  const barColors = months.map((m) => colorForYm(m.ym, sortedNotes));
  // Poznámky, jejichž měsíc se v grafu zobrazuje — první výskyt barvy per
  // měsíc, ať se stejná poznámka neopakuje v legendě víckrát.
  const notesInView = sortedNotes.filter((n) =>
    months.some((m) => m.ym === n.note_date.slice(0, 7)),
  );

  return (
    <div className="w-full relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto block"
        preserveAspectRatio="xMidYMid meet"
      >
        <title>Podíl objednávek s přednostní expedicí po měsících</title>
        {ticks.map((t) => {
          const yy = barY(t);
          return (
            <g key={t}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={yy}
                y2={yy}
                stroke="currentColor"
                strokeOpacity="0.1"
              />
              <text
                x={PAD.l - 6}
                y={yy + 3}
                textAnchor="end"
                className="text-[10px] fill-current text-text-muted"
              >
                {t.toFixed(0)}%
              </text>
            </g>
          );
        })}

        {months.map((m, i) => {
          const h = innerH - (barY(m.pct) - PAD.t);
          const isHover = hoverIdx === i;
          const hasNote = notesInView.some((n) => n.note_date.slice(0, 7) === m.ym);
          return (
            <g key={m.ym}>
              {hasNote && (
                <text
                  x={barX(i) + innerW / months.length / 2}
                  y={PAD.t - 4}
                  textAnchor="middle"
                  className="text-[10px]"
                  fill={barColors[i]}
                >
                  ●
                </text>
              )}
              <rect
                x={barX(i) + barGap / 2}
                y={barY(m.pct)}
                width={Math.max(0, barW)}
                height={Math.max(0, h)}
                rx="2"
                fill={barColors[i]}
                fillOpacity={isHover ? 1 : 0.75}
              />
              {/* Neviditelný hit-target přes celý sloupec — snazší hover než na tenký bar. */}
              <rect
                x={barX(i)}
                y={PAD.t}
                width={innerW / months.length}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
              />
              <text
                x={barX(i) + innerW / months.length / 2}
                y={H - PAD.b + 16}
                textAnchor="middle"
                className="text-[10px] fill-current text-text-muted"
              >
                {formatMonthShort(m.ym)}
              </text>
            </g>
          );
        })}
      </svg>

      {hovered && (
        <div
          className="absolute pointer-events-none bg-text text-white text-xs font-medium px-2.5 py-1.5 rounded shadow-card-hover whitespace-nowrap"
          style={{
            left: `${((barX(hoverIdx ?? 0) + innerW / months.length / 2) / W) * 100}%`,
            top: `${(barY(hovered.pct) / H) * 100}%`,
            transform: 'translate(-50%, calc(-100% - 10px))',
          }}
        >
          <div>{formatMonthShort(hovered.ym)}</div>
          <div className="text-[13px] font-bold">
            {hovered.pct.toFixed(1)}% ({hovered.with_service} / {hovered.total_orders})
          </div>
          <div className="text-[11px] text-white/80">
            {formatCzk(hovered.revenue_without_vat)} bez DPH
          </div>
          {notesInView
            .filter((n) => n.note_date.slice(0, 7) === hovered.ym)
            .map((n) => (
              <div
                key={n.id}
                className="text-[11px] mt-1 pt-1 border-t border-white/20 max-w-[220px]"
              >
                <span style={{ color: n.color }}>●</span> {n.note}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Products tab ───────────────────────────────────────────────────────

type ProductRow = {
  name: string;
  count: number;
  revenue: number;
  avg_price: number;
  image_url: string | null;
  parent_code: string | null;
  url: string | null;
};

type RelatedProduct = {
  code: string | null;
  name: string;
  co_count: number;
  image_url: string | null;
  url: string | null;
};

type RelatedResult = {
  target_order_count: number;
  related: RelatedProduct[];
};

function ProductsTab({ range }: { range: Range }) {
  const [source, setSource] = useState<'ga4' | 'shoptet'>('shoptet');
  const [products, setProducts] = useState<ProductRow[] | null>(null);
  const [totals, setTotals] = useState<{ count: number; revenue: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'count' | 'revenue'>('revenue');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const PAGE_SIZE = 50;

  // Související produkty (co-occurrence) — rozbalovací panel pod řádkem.
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [relatedCache, setRelatedCache] = useState<Record<string, RelatedResult>>({});
  const [relatedLoading, setRelatedLoading] = useState<string | null>(null);
  const [relatedErr, setRelatedErr] = useState<string | null>(null);

  // Změna období / zdroje → zahoď cache souvisejících a sbal panel.
  useEffect(() => {
    setExpandedCode(null);
    setRelatedCache({});
    setRelatedErr(null);
    setPage(0);
  }, [range.startDate, range.endDate, source]);

  async function toggleRelated(code: string) {
    if (expandedCode === code) {
      setExpandedCode(null);
      return;
    }
    setExpandedCode(code);
    setRelatedErr(null);
    if (relatedCache[code]) return; // už načteno
    setRelatedLoading(code);
    try {
      const params = new URLSearchParams({
        code,
        from: range.startDate,
        to: range.endDate,
      });
      const res = await apiFetch<RelatedResult>(
        `/api/analytics/products/related?${params.toString()}`,
      );
      setRelatedCache((prev) => ({ ...prev, [code]: res }));
    } catch (e: unknown) {
      setRelatedErr(e instanceof ApiError ? e.describe() : 'Chyba načítání souvisejících produktů');
    } finally {
      setRelatedLoading(null);
    }
  }

  function toggleSort(col: 'count' | 'revenue') {
    setPage(0);
    if (sortBy === col) {
      setOrderDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(col);
      setOrderDir('desc');
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const params = new URLSearchParams({
          from: range.startDate,
          to: range.endDate,
          limit: String(PAGE_SIZE),
          offset: String(page * PAGE_SIZE),
          source,
          sort: sortBy,
          order: orderDir,
        });
        const res = await apiFetch<{
          products: ProductRow[];
          totals: { count: number; revenue: number };
          total_products: number;
        }>(`/api/analytics/products?${params.toString()}`);
        if (!cancelled) {
          setProducts(res.products);
          setTotals(res.totals);
          setTotalProducts(res.total_products ?? 0);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(e instanceof ApiError ? e.describe() : 'Chyba načítání');
          setProducts(null);
          setTotals(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range.startDate, range.endDate, source, sortBy, orderDir, page]);

  const totalCount = totals?.count ?? 0;
  const totalRevenue = totals?.revenue ?? 0;
  const pageCount = Math.max(Math.ceil(totalProducts / PAGE_SIZE), 1);

  return (
    <>
      <div className="bg-surface rounded-xl shadow-card">
        <div className="flex items-baseline justify-between px-5 pt-5 pb-2 flex-wrap gap-2">
          <div>
            <h2 className="text-base font-bold text-text">Prodané produkty</h2>
            {totals && totalCount > 0 && (
              <p className="text-[11px] text-text-muted mt-0.5">
                Celkem za období: <strong className="text-text">{formatNum(totalCount)}</strong>{' '}
                kusů · <strong className="text-text">{formatCzk(totalRevenue)}</strong> ·{' '}
                <strong className="text-text">{formatNum(totalProducts)}</strong> produktů
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-bg border border-border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setSource('shoptet')}
                className={`text-xs font-medium px-3 py-1.5 cursor-pointer transition-colors ${
                  source === 'shoptet'
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:bg-surface-hover'
                }`}
                title="Přesná data z reálných objednávek (Shoptet REST API)"
              >
                Shoptet (přesné)
              </button>
              <button
                type="button"
                onClick={() => setSource('ga4')}
                className={`text-xs font-medium px-3 py-1.5 cursor-pointer transition-colors ${
                  source === 'ga4'
                    ? 'bg-primary text-white'
                    : 'text-text-secondary hover:bg-surface-hover'
                }`}
                title="Tracking z GA4 — ~10-30% under-tracking, ale zahrnuje purchase events"
              >
                GA4
              </button>
            </div>
          </div>
        </div>
        {err && (
          <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded mx-5 mb-3 px-4 py-3">
            {err}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg border-b border-border text-text-secondary">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5 w-12">#</th>
                <th className="text-left font-semibold px-4 py-2.5">Produkt</th>
                <th className="text-right font-semibold px-4 py-2.5 w-36">
                  <button
                    type="button"
                    onClick={() => toggleSort('count')}
                    className="inline-flex items-center gap-1 hover:text-text cursor-pointer ml-auto"
                  >
                    Počet
                    <span className="text-[10px] opacity-70">
                      {sortBy === 'count' ? (orderDir === 'desc' ? '▼' : '▲') : '↕'}
                    </span>
                  </button>
                </th>
                <th className="text-right font-semibold px-4 py-2.5 w-44">
                  <button
                    type="button"
                    onClick={() => toggleSort('revenue')}
                    className="inline-flex items-center gap-1 hover:text-text cursor-pointer ml-auto"
                  >
                    Tržby (Kč)
                    <span className="text-[10px] opacity-70">
                      {sortBy === 'revenue' ? (orderDir === 'desc' ? '▼' : '▲') : '↕'}
                    </span>
                  </button>
                </th>
                <th className="text-right font-semibold px-4 py-2.5 w-32">Prům. cena</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center text-text-muted py-8">
                    Načítám…
                  </td>
                </tr>
              )}
              {!loading &&
                products &&
                products.length > 0 &&
                products.map((p, i) => {
                  const countPct = totalCount > 0 ? (p.count / totalCount) * 100 : 0;
                  const revPct = totalRevenue > 0 ? (p.revenue / totalRevenue) * 100 : 0;
                  const code = p.parent_code;
                  // Související produkty mají smysl jen pro Shoptet (reálné objednávky).
                  const clickable = source === 'shoptet' && !!code;
                  const isOpen = clickable && expandedCode === code;
                  return (
                    <Fragment key={`${p.name}-${i}`}>
                      <tr
                        onClick={clickable && code ? () => toggleRelated(code) : undefined}
                        className={`border-b border-border hover:bg-bg/50 ${
                          isOpen ? '' : 'last:border-0'
                        } ${clickable ? 'cursor-pointer' : ''}`}
                      >
                        <td className="px-4 py-2.5 text-text-muted align-middle">
                          <span className="flex items-center gap-1">
                            {clickable && (
                              <span className="text-[10px] text-text-muted/70 w-2">
                                {isOpen ? '▾' : '▸'}
                              </span>
                            )}
                            {page * PAGE_SIZE + i + 1}.
                          </span>
                        </td>
                        <td className="px-4 py-2.5 align-middle">
                          <div className="flex items-center gap-3">
                            {p.image_url ? (
                              <img
                                src={p.image_url}
                                alt=""
                                loading="lazy"
                                className="w-10 h-10 rounded object-contain bg-bg border border-border flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded bg-bg border border-border flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              {p.url ? (
                                <a
                                  href={p.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-text font-medium hover:text-primary truncate block"
                                >
                                  {p.name}
                                </a>
                              ) : (
                                <div className="text-text font-medium truncate">{p.name}</div>
                              )}
                              {p.parent_code && (
                                <div className="text-[11px] text-text-muted font-mono">
                                  {p.parent_code}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right align-middle">
                          <div className="font-semibold text-text">{formatNum(p.count)}</div>
                          <div className="text-[11px] text-text-muted">{countPct.toFixed(1)}%</div>
                        </td>
                        <td className="px-4 py-2.5 text-right align-middle">
                          <div className="text-text">{formatCzk(p.revenue)}</div>
                          <div className="text-[11px] text-text-muted">{revPct.toFixed(1)}%</div>
                        </td>
                        <td className="px-4 py-2.5 text-right text-text-secondary align-middle">
                          {formatCzk(p.avg_price)}
                        </td>
                      </tr>
                      {isOpen && code && (
                        <tr className="border-b border-border last:border-0">
                          <td colSpan={5} className="bg-bg/40 px-4 py-3">
                            <RelatedPanel
                              loading={relatedLoading === code}
                              err={relatedErr}
                              data={relatedCache[code]}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              {!loading && (!products || products.length === 0) && !err && (
                <tr>
                  <td colSpan={5} className="text-center text-text-muted py-8">
                    Žádné produktové prodeje za vybrané období.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {totalProducts > PAGE_SIZE && (
          <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-3 border-t border-border">
            <div className="text-xs text-text-muted">
              Strana <strong className="text-text">{page + 1}</strong> /{' '}
              <strong className="text-text">{pageCount}</strong>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(0)}
                disabled={page === 0 || loading}
                className="text-xs font-medium text-text-secondary bg-bg border border-border rounded px-2.5 py-1.5 hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                «
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
                disabled={page === 0 || loading}
                className="text-xs font-medium text-text-secondary bg-bg border border-border rounded px-2.5 py-1.5 hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                ‹ Předchozí
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
                disabled={page >= pageCount - 1 || loading}
                className="text-xs font-medium text-text-secondary bg-bg border border-border rounded px-2.5 py-1.5 hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                Další ›
              </button>
              <button
                type="button"
                onClick={() => setPage(pageCount - 1)}
                disabled={page >= pageCount - 1 || loading}
                className="text-xs font-medium text-text-secondary bg-bg border border-border rounded px-2.5 py-1.5 hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Rozbalovací panel pod produktem — TOP 6 nejčastěji spolu objednávaných.
function RelatedPanel({
  loading,
  err,
  data,
}: {
  loading: boolean;
  err: string | null;
  data: RelatedResult | undefined;
}) {
  if (loading) {
    return <div className="text-xs text-text-muted py-2">Načítám související produkty…</div>;
  }
  if (!data) {
    return err ? <div className="text-xs text-danger py-2">{err}</div> : null;
  }
  if (data.related.length === 0) {
    return (
      <div className="text-xs text-text-muted py-2">
        Žádné produkty se v tomto období neobjevily v objednávce společně.
      </div>
    );
  }
  return (
    <div>
      <div className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-2">
        Nejčastěji v objednávce spolu — TOP 6
        <span className="text-text-muted normal-case font-normal">
          {' '}
          (z {formatNum(data.target_order_count)} objednávek s tímto produktem)
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {data.related.map((r, idx) => {
          const pct =
            data.target_order_count > 0 ? (r.co_count / data.target_order_count) * 100 : 0;
          const inner = (
            <>
              {r.image_url ? (
                <img
                  src={r.image_url}
                  alt=""
                  loading="lazy"
                  className="w-full h-20 rounded object-contain bg-surface border border-border"
                />
              ) : (
                <div className="w-full h-20 rounded bg-surface border border-border" />
              )}
              <div className="text-[11px] text-text font-medium mt-1 line-clamp-2">{r.name}</div>
              <div className="text-[11px] text-text-muted mt-0.5">
                {formatNum(r.co_count)}× · {pct.toFixed(0)} %
              </div>
            </>
          );
          return r.url ? (
            <a
              key={r.code ?? idx}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`Ve ${r.co_count} objednávkách spolu (${pct.toFixed(0)} % objednávek s vybraným produktem)`}
              className="block hover:opacity-80 transition-opacity"
            >
              {inner}
            </a>
          ) : (
            <div key={r.code ?? idx}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  ga4Ref,
  value,
  loading,
  onClick,
}: {
  label: string;
  ga4Ref?: string;
  value: string;
  loading: boolean;
  onClick?: () => void;
}) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`bg-surface rounded-xl p-4 shadow-card text-left ${
        onClick ? 'hover:shadow-card-hover hover:bg-bg/30 cursor-pointer transition-all' : ''
      } w-full`}
    >
      <div className="flex items-baseline gap-1.5 flex-wrap">
        <div className="text-xs font-medium text-text-muted uppercase tracking-wider">{label}</div>
        {ga4Ref && (
          <span className="text-[10px] font-mono text-text-muted/70 normal-case">{ga4Ref}</span>
        )}
      </div>
      <div className={`text-2xl font-bold text-text mt-1 ${loading ? 'animate-pulse' : ''}`}>
        {value}
      </div>
    </Wrapper>
  );
}

function CartConvInfo() {
  return (
    <div className="bg-bg border border-border rounded-lg p-4 mb-4 text-xs text-text-secondary leading-relaxed">
      <div className="font-semibold text-text mb-2">Jak se počítají hodnoty</div>
      <p className="mb-2">
        Obě hodnoty jsou <strong className="text-text">unikátní uživatelé</strong> (GA4 metrika{' '}
        <code className="font-mono text-text-muted">totalUsers</code>) za zvolené období, ne počet
        kliknutí ani relací.
      </p>
      <ul className="space-y-1 pl-1 mb-2">
        <li>
          <strong className="text-text">Návštěvníci</strong> ={' '}
          <span className="font-mono text-text-muted">totalUsers</span> bez filtru — všichni
          unikátní uživatelé, kteří kamkoli na e-shop přišli.
        </li>
        <li>
          <strong className="text-text">Přidání do košíku (klik)</strong> ={' '}
          <span className="font-mono text-text-muted">totalUsers</span> s filtrem{' '}
          <span className="font-mono text-text-muted">eventName = add_to_cart</span> — uživatelé,
          kteří aspoň jednou spustili add_to_cart event (klik na tlačítko „Přidat do košíku").
        </li>
      </ul>
      <div className="font-semibold text-text mb-1">Konkrétní scénáře:</div>
      <ul className="space-y-0.5 pl-1 mb-2">
        <li>
          1 zákazník přidá 10 produktů → <strong className="text-text">1 uživatel</strong> (každý
          user_id se počítá jednou, bez ohledu na počet kliknutí).
        </li>
        <li>
          1 zákazník přidá 1 produkt → <strong className="text-text">1 uživatel</strong>.
        </li>
        <li>
          Otevře jen produkt a nepřidá → <strong className="text-text">0 uživatelů</strong> (event
          add_to_cart se nespustil).
        </li>
        <li>
          Vrátí se další den ze stejného zařízení → pořád{' '}
          <strong className="text-text">1 uživatel</strong>.
        </li>
      </ul>
      <p>
        Konverze <strong className="text-text">X% z návštěvníků</strong> odpovídá na: „Jaké procento
        návštěvníků si do košíku přidalo alespoň něco?". Hodnota je přesně to, co chceš měřit u
        sticky add-to-cart tlačítka — kolik víc lidí vůbec začne přidávat (ne kolik kusů přidají).
      </p>
    </div>
  );
}

function FunnelInfo() {
  return (
    <div className="bg-bg border border-border rounded-lg p-4 mb-4 text-xs text-text-secondary leading-relaxed">
      <div className="font-semibold text-text mb-2">Jak se počítají hodnoty</div>
      <p className="mb-2">
        Všechny hodnoty jsou <strong className="text-text">unikátní uživatelé</strong> (GA4 metrika{' '}
        <code className="font-mono text-text-muted">totalUsers</code>) za zvolené období, ne počet
        kliknutí ani relací.
      </p>
      <ul className="space-y-1 pl-1">
        <li>
          <strong className="text-text">Přidání do košíku</strong> ={' '}
          <span className="font-mono text-text-muted">totalUsers</span> s filtrem{' '}
          <span className="font-mono text-text-muted">pagePath = /kosik/</span> — uživatelé, kteří
          se dostali na košíkovou stránku. (Není to klik na tlačítko „Přidat do košíku", to je jiný
          event <span className="font-mono text-text-muted">add_to_cart</span>.)
        </li>
        <li>
          <strong className="text-text">Přechod na dopravu</strong> = uživatelé na{' '}
          <span className="font-mono text-text-muted">/objednavka/krok-1/</span>.
        </li>
        <li>
          <strong className="text-text">Údaje o zákazníkovi</strong> = uživatelé na{' '}
          <span className="font-mono text-text-muted">/objednavka/krok-2/</span>.
        </li>
        <li>
          <strong className="text-text">Objednávka</strong> = uživatelé, kteří spustili GA4 event{' '}
          <span className="font-mono text-text-muted">purchase</span> (skutečně dokončili
          transakci).
        </li>
      </ul>
      <p className="mt-2">
        Procenta v lištách = % z prvního kroku (košíku). Klikem na krok se otevře denní trend; pro
        2.–4. krok je v něm <strong className="text-text">denní % z košíku</strong> (kolik z denních
        návštěvníků košíku se ten den dostalo dál), pro Přidání do košíku denní počet uživatelů.
      </p>
    </div>
  );
}

function FunnelStatRow({
  label,
  ga4Ref,
  value,
  subRight,
  onClick,
}: {
  label: string;
  ga4Ref?: string;
  value: number;
  subRight?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left hover:bg-bg/30 rounded-md p-2 -m-2 cursor-pointer transition-colors"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-text flex items-baseline gap-2">
          {label}
          {ga4Ref && <span className="text-[11px] font-mono text-text-muted/70">{ga4Ref}</span>}
        </span>
        <span className="text-sm text-text-secondary whitespace-nowrap">
          <strong className="text-text">{formatNum(value)}</strong> uživatelů
          {subRight && <span className="text-text-muted ml-2">({subRight})</span>}
        </span>
      </div>
    </button>
  );
}

function Funnel({
  data,
  onStepClick,
}: {
  data: FunnelData;
  onStepClick: (q: TrendQuery) => void;
}) {
  type Step = { name: string; ga4Ref: string; value: number; query: TrendQuery };
  const steps: Step[] = [
    {
      name: 'Přidání do košíku',
      ga4Ref: '/kosik/',
      value: data.cart,
      // Cart je base funnelu, % z košíku by bylo vždy 100% → necháváme count
      query: {
        metric: 'pageview',
        pagePath: '/kosik/',
        label: 'Přidání do košíku',
      },
    },
    {
      name: 'Přechod na dopravu',
      ga4Ref: '/objednavka/krok-1/',
      value: data.shipping,
      query: {
        metric: 'funnel_pct',
        step: 'shipping',
        label: 'Přechod na dopravu',
      },
    },
    {
      name: 'Údaje o zákazníkovi',
      ga4Ref: '/objednavka/krok-2/',
      value: data.customer,
      query: {
        metric: 'funnel_pct',
        step: 'customer',
        label: 'Údaje o zákazníkovi',
      },
    },
    {
      name: 'Objednávka',
      ga4Ref: 'event: purchase',
      value: data.purchase,
      query: { metric: 'funnel_pct', step: 'purchase', label: 'Objednávka' },
    },
  ];
  const top = steps[0]?.value ?? 0;
  return (
    <div className="space-y-3">
      {steps.map((s, i) => {
        const pct = top > 0 ? (s.value / top) * 100 : 0;
        const prev = i > 0 ? (steps[i - 1]?.value ?? 0) : 0;
        const dropPct = i > 0 && prev > 0 ? -(((prev - s.value) / prev) * 100) : 0;
        return (
          <button
            type="button"
            key={s.name}
            onClick={() => onStepClick(s.query)}
            className="block w-full text-left hover:bg-bg/30 rounded-md p-1 -m-1 cursor-pointer transition-colors"
          >
            <div className="flex items-baseline justify-between mb-1 gap-3">
              <span className="text-sm text-text flex items-baseline gap-2">
                {s.name}
                <span className="text-[11px] font-mono text-text-muted/70">{s.ga4Ref}</span>
              </span>
              <span className="text-sm text-text-secondary whitespace-nowrap">
                {formatNum(s.value)} uživatelů
              </span>
            </div>
            <div className="bg-bg rounded-md overflow-hidden h-7 relative">
              <div
                className="bg-primary h-full transition-all"
                style={{ width: `${pct.toFixed(1)}%` }}
              />
              <span className="absolute inset-0 flex items-center px-3 text-xs font-semibold text-white">
                {pct.toFixed(1)}%
              </span>
            </div>
            {i > 0 && dropPct < 0 && (
              <div className="text-[11px] text-danger mt-0.5">{dropPct.toFixed(1)}% odpad</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Trend modal s SVG line chartem ─────────────────────────────────────

function TrendModal({
  query,
  range,
  notes,
  onNotesChanged,
  onClose,
}: {
  query: TrendQuery;
  range: Range;
  notes: AnalyticsNote[];
  onNotesChanged: () => void;
  onClose: () => void;
}) {
  const [data, setData] = useState<TrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Filtr na zařízení — dostupný jen u metrik, které ho podporují (zatím add_to_cart konverze).
  const supportsDevice = query.metric === 'visitor_to_cart_pct';
  const [device, setDevice] = useState<'all' | 'desktop' | 'mobile'>('all');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const params = new URLSearchParams({
          metric: query.metric,
          from: range.startDate,
          to: range.endDate,
        });
        if (query.eventName) params.set('eventName', query.eventName);
        if (query.pagePath) params.set('pagePath', query.pagePath);
        if (query.step) params.set('step', query.step);
        if (supportsDevice && device !== 'all') params.set('device', device);
        const res = await apiFetch<TrendResponse>(`/api/analytics/trend?${params.toString()}`);
        if (!cancelled) setData(res);
      } catch (e: unknown) {
        if (!cancelled) {
          setErr(e instanceof ApiError ? e.describe() : 'Chyba načítání trendu');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    query.metric,
    query.eventName,
    query.pagePath,
    query.step,
    range.startDate,
    range.endDate,
    supportsDevice,
    device,
  ]);

  const total = data ? data.series.reduce((s, p) => s + p.value, 0) : 0;
  const avg = data && data.series.length > 0 ? total / data.series.length : 0;
  const peak =
    data && data.series.length > 0
      ? data.series.reduce(
          (a, b) => (b.value > a.value ? b : a),
          data.series[0] ?? { date: '', value: 0 },
        )
      : null;

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-surface rounded-xl shadow-card-hover w-full max-w-6xl reveal my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-text">{query.label} — trend</h2>
            <p className="text-xs text-text-muted mt-0.5">
              {formatDateCs(range.startDate)} — {formatDateCs(range.endDate)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text text-2xl leading-none cursor-pointer"
            aria-label="Zavřít"
          >
            ×
          </button>
        </div>

        {supportsDevice && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-text-muted">Zařízení:</span>
            <div className="inline-flex rounded-lg border border-border overflow-hidden">
              {(
                [
                  { key: 'all', label: 'Vše' },
                  { key: 'desktop', label: 'Počítač' },
                  { key: 'mobile', label: 'Mobil' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setDevice(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
                    device === opt.key
                      ? 'bg-primary text-white'
                      : 'bg-surface text-text-secondary hover:bg-bg'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading && <div className="text-sm text-text-muted py-12 text-center">Načítám…</div>}
        {err && (
          <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3">
            {err}
          </div>
        )}
        {data && !loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Hlavní obsah: stats + graf */}
            <div className="lg:col-span-2">
              {/* Souhrn — Celkem nedává smysl pro ratio metriky */}
              <div
                className={`grid gap-3 mb-4 ${
                  query.metric === 'cr' ||
                  query.metric === 'aov' ||
                  query.metric === 'funnel_pct' ||
                  query.metric === 'visitor_to_cart_pct'
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
                }`}
              >
                {query.metric !== 'cr' &&
                  query.metric !== 'aov' &&
                  query.metric !== 'funnel_pct' &&
                  query.metric !== 'visitor_to_cart_pct' && (
                    <SmallStat label="Celkem" value={formatTrendValue(total, data.unit)} />
                  )}
                <SmallStat label="Průměr/den" value={formatTrendValue(avg, data.unit)} />
                <SmallStat
                  label="Nejvyšší den"
                  value={
                    peak && peak.date
                      ? `${formatTrendValue(peak.value, data.unit)} (${formatDateCs(peak.date)})`
                      : '—'
                  }
                />
              </div>

              <LineChart series={data.series} unit={data.unit} notes={notes} />
            </div>

            {/* Side-panel s poznámkami */}
            <NotesPanel notes={notes} range={range} onChanged={onNotesChanged} />
          </div>
        )}
      </div>
    </div>
  );
}

function NotesPanel({
  notes,
  range,
  onChanged,
  scope = 'general',
}: {
  notes: AnalyticsNote[];
  range: Range;
  onChanged: () => void;
  /** Do kterého grafu poznámka patří — viz komentář u NOTE_SCOPE_RE na backendu. */
  scope?: string;
}) {
  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [newText, setNewText] = useState('');
  const [newColor, setNewColor] = useState<string>(NOTE_COLORS[0]?.hex ?? '#ef4444');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Poznámky v aktuálním date range, sorted desc (nejnovější nahoře)
  const inRange = notes
    .filter((n) => n.note_date >= range.startDate && n.note_date <= range.endDate)
    .sort((a, b) => (a.note_date < b.note_date ? 1 : -1));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      await apiFetch('/api/analytics/notes', {
        method: 'POST',
        body: { note_date: newDate, note: newText.trim(), color: newColor, scope },
      });
      setNewText('');
      setNewDate(new Date().toISOString().slice(0, 10));
      setAdding(false);
      onChanged();
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: number) {
    if (!confirm('Smazat poznámku?')) return;
    try {
      await apiFetch(`/api/analytics/notes/${id}`, { method: 'DELETE' });
      onChanged();
    } catch (e: unknown) {
      alert(e instanceof ApiError ? e.describe() : 'Smazání selhalo');
    }
  }

  return (
    <div className="bg-bg border border-border rounded-lg p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-bold text-text">Poznámky / změny</h3>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs font-medium text-primary hover:text-primary-dark cursor-pointer"
          >
            + Přidat
          </button>
        )}
      </div>

      {adding && (
        <form
          onSubmit={submit}
          className="space-y-2 mb-3 p-3 bg-surface rounded border border-border"
        >
          <div>
            <label className="text-[11px] font-medium text-text-secondary block mb-0.5">
              Datum změny
            </label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              required
              className="w-full text-sm text-text bg-bg border border-border rounded px-2 py-1.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-text-secondary block mb-0.5">
              Poznámka
            </label>
            <input
              type="text"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              required
              maxLength={300}
              autoFocus
              placeholder="Např. Nasazen sticky CTA"
              className="w-full text-sm text-text bg-bg border border-border rounded px-2 py-1.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-text-secondary block mb-1">Barva</label>
            <div className="flex flex-wrap gap-1.5">
              {NOTE_COLORS.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setNewColor(c.hex)}
                  title={c.name}
                  className={`w-6 h-6 rounded-full border-2 transition-all cursor-pointer ${
                    newColor === c.hex
                      ? 'ring-2 ring-offset-1 ring-text border-white scale-110'
                      : 'border-white'
                  }`}
                  style={{ backgroundColor: c.hex }}
                  aria-label={c.name}
                />
              ))}
            </div>
          </div>
          {err && (
            <div className="text-xs text-danger bg-danger-light border border-danger/30 rounded px-2 py-1">
              {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setErr(null);
              }}
              className="text-xs font-medium text-text-secondary hover:text-text cursor-pointer px-2"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving || !newText.trim()}
              className="bg-action hover:bg-action-dark text-white text-xs font-semibold rounded px-3 py-1.5 cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
          </div>
        </form>
      )}

      {/* Seznam poznámek v range */}
      {inRange.length === 0 && !adding && (
        <div className="text-xs text-text-muted py-4 text-center">
          Žádné poznámky v období.
          <br />
          Klikni na „+ Přidat" pro evidenci změny.
        </div>
      )}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {inRange.map((n) => (
          <div
            key={n.id}
            className="flex items-start gap-2 p-2 bg-surface rounded border border-border"
          >
            <div
              className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
              style={{ backgroundColor: n.color }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-semibold text-text-secondary">
                {formatDateCs(n.note_date)}
              </div>
              <div className="text-sm text-text break-words">{n.note}</div>
            </div>
            <button
              type="button"
              onClick={() => remove(n.id)}
              className="text-text-muted hover:text-danger text-sm leading-none cursor-pointer px-1"
              aria-label="Smazat"
              title="Smazat poznámku"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-bg rounded-lg p-3 border border-border">
      <div className="text-[11px] font-medium text-text-muted uppercase tracking-wider">
        {label}
      </div>
      <div className="text-sm font-bold text-text mt-0.5 truncate">{value}</div>
    </div>
  );
}

function formatTrendValue(v: number, unit: TrendUnit): string {
  if (unit === 'currency') return formatCzk(v);
  if (unit === 'percent') return formatPct(v);
  return formatNum(v);
}

// Pro datum najdi barvu aktivní poznámky (nejnovější s datem <= cílovým).
// Pokud žádná nepasuje, vrátí defaultní modrou.
function colorForDate(date: string, sortedNotes: AnalyticsNote[]): string {
  let active = DEFAULT_LINE_COLOR;
  for (const n of sortedNotes) {
    if (n.note_date <= date) active = n.color;
    else break;
  }
  return active;
}

function LineChart({
  series,
  unit,
  notes = [],
}: {
  series: Array<{ date: string; value: number }>;
  unit: TrendUnit;
  notes?: AnalyticsNote[];
}) {
  const W = 700;
  const H = 280;
  const PAD = { l: 50, r: 16, t: 16, b: 32 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (series.length === 0) {
    return <div className="text-sm text-text-muted py-12 text-center">Žádná data.</div>;
  }

  const max = Math.max(...series.map((p) => p.value), 1);
  const min = 0;
  const range = max - min || 1;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : innerW;

  function x(i: number) {
    return PAD.l + i * stepX;
  }
  function y(v: number) {
    return PAD.t + innerH - ((v - min) / range) * innerH;
  }

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!svgRef.current || series.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left;
    const svgX = (pxX / rect.width) * W;
    const idx = Math.round((svgX - PAD.l) / (stepX || 1));
    if (idx < 0 || idx > series.length - 1) {
      setHoverIdx(null);
      return;
    }
    setHoverIdx(idx);
  }

  const path = series
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`)
    .join(' ');
  const areaPath = `${path} L ${x(series.length - 1).toFixed(1)} ${y(min).toFixed(1)} L ${x(0).toFixed(1)} ${y(min).toFixed(1)} Z`;

  // Segmentace čáry podle aktivní poznámky.
  // Sorted notes ASC podle data — pro colorForDate procházení.
  const sortedNotes = [...notes].sort((a, b) => (a.note_date < b.note_date ? -1 : 1));
  // Pro každý bod spočítáme aktivní barvu.
  const pointColors = series.map((p) => colorForDate(p.date, sortedNotes));
  // Vybuduj segmenty: každý segment = path of 2+ points stejné barvy, s první bodem
  // sdíleným s předchozím segmentem (aby čára byla spojitá).
  const lineSegments: Array<{ color: string; d: string }> = [];
  if (series.length >= 2) {
    let segColor = pointColors[1] ?? DEFAULT_LINE_COLOR;
    let segPath = `M ${x(0).toFixed(1)} ${y(series[0]!.value).toFixed(1)}`;
    for (let i = 1; i < series.length; i++) {
      const colorAt = pointColors[i] ?? DEFAULT_LINE_COLOR;
      if (colorAt !== segColor) {
        // Uzavřeme aktuální segment u (i-1) a začneme nový od (i-1) novou barvou
        lineSegments.push({ color: segColor, d: segPath });
        segColor = colorAt;
        segPath = `M ${x(i - 1).toFixed(1)} ${y(series[i - 1]!.value).toFixed(1)}`;
      }
      segPath += ` L ${x(i).toFixed(1)} ${y(series[i]!.value).toFixed(1)}`;
    }
    lineSegments.push({ color: segColor, d: segPath });
  } else if (series.length === 1) {
    // Single point — žádná čára.
  }

  // Vertikální markery pro poznámky v zobrazeném rozsahu
  const noteMarkers = sortedNotes
    .map((n) => {
      const idx = series.findIndex((p) => p.date === n.note_date);
      if (idx < 0) return null;
      return { idx, note: n };
    })
    .filter((v): v is { idx: number; note: AnalyticsNote } => v !== null);

  // Y-axis ticks (4 hodnoty)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => min + t * range);
  // X-axis labels: ~5 rozumně rozložených
  const xLabelEvery = Math.max(1, Math.floor(series.length / 6));

  const hovered = hoverIdx !== null ? series[hoverIdx] : null;
  const hoverX = hovered && hoverIdx !== null ? x(hoverIdx) : 0;
  const hoverY = hovered ? y(hovered.value) : 0;
  // Pozice tooltip nad/pod bodem podle toho, kde se vejde
  const tipBelow = hoverY < H * 0.3;
  const tipRight = hoverX > W * 0.7;
  const tipLeft = hoverX < W * 0.15;

  return (
    <div className="w-full relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto block cursor-crosshair"
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Y grid + labels */}
        {ticks.map((t, i) => {
          const yy = y(t);
          return (
            <g key={i}>
              <line
                x1={PAD.l}
                x2={W - PAD.r}
                y1={yy}
                y2={yy}
                stroke="currentColor"
                strokeOpacity="0.1"
              />
              <text
                x={PAD.l - 6}
                y={yy + 3}
                textAnchor="end"
                className="text-[10px] fill-current text-text-muted"
              >
                {formatTrendValue(t, unit)}
              </text>
            </g>
          );
        })}

        {/* Area (jednobarevná default, ne segmentujem kvůli jednoduchosti) */}
        <path d={areaPath} fill={DEFAULT_LINE_COLOR} fillOpacity="0.08" />

        {/* Vertikální markery poznámek */}
        {noteMarkers.map(({ idx, note }) => (
          <g key={note.id} pointerEvents="none">
            <line
              x1={x(idx)}
              x2={x(idx)}
              y1={PAD.t}
              y2={H - PAD.b}
              stroke={note.color}
              strokeOpacity="0.35"
              strokeDasharray="4 3"
              strokeWidth="1.5"
            />
          </g>
        ))}

        {/* Line — segmentovaná podle barev poznámek */}
        {lineSegments.map((seg, si) => (
          <path key={si} d={seg.d} fill="none" stroke={seg.color} strokeWidth="2" />
        ))}

        {/* Body (malé tečky) — barva podle aktivní poznámky pro daný bod */}
        {series.map((p, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(p.value)}
            r="3"
            fill={pointColors[i] ?? DEFAULT_LINE_COLOR}
            pointerEvents="none"
          />
        ))}

        {/* Hover indikátor: vertikální čára + velký bod (barva podle aktivní poznámky) */}
        {hovered && hoverIdx !== null && (
          <g pointerEvents="none">
            <line
              x1={hoverX}
              x2={hoverX}
              y1={PAD.t}
              y2={H - PAD.b}
              stroke={pointColors[hoverIdx] ?? DEFAULT_LINE_COLOR}
              strokeOpacity="0.3"
              strokeDasharray="3 3"
            />
            <circle
              cx={hoverX}
              cy={hoverY}
              r="6"
              fill={pointColors[hoverIdx] ?? DEFAULT_LINE_COLOR}
              stroke="white"
              strokeWidth="2"
            />
          </g>
        )}

        {/* X labels */}
        {series.map((p, i) => {
          if (i % xLabelEvery !== 0 && i !== series.length - 1) return null;
          return (
            <text
              key={`xl-${i}`}
              x={x(i)}
              y={H - PAD.b + 16}
              textAnchor="middle"
              className="text-[10px] fill-current text-text-muted"
            >
              {formatDateShort(p.date)}
            </text>
          );
        })}
      </svg>

      {/* Vlastní tooltip — bez delaye, sleduje hover bod */}
      {hovered && (
        <div
          className="absolute pointer-events-none bg-text text-white text-xs font-medium px-2.5 py-1.5 rounded shadow-card-hover whitespace-nowrap"
          style={{
            left: `${(hoverX / W) * 100}%`,
            top: `${(hoverY / H) * 100}%`,
            transform: `translate(${
              tipRight ? 'calc(-100% - 12px)' : tipLeft ? '12px' : '-50%'
            }, ${tipBelow ? '12px' : 'calc(-100% - 12px)'})`,
          }}
        >
          <div>{formatDateCs(hovered.date)}</div>
          <div className="text-[13px] font-bold">{formatTrendValue(hovered.value, unit)}</div>
        </div>
      )}
    </div>
  );
}

function CrBadge({ cr }: { cr: number }) {
  const cls =
    cr >= 2.5
      ? 'text-success bg-success-light'
      : cr >= 1.5
        ? 'text-warning bg-warning-light'
        : 'text-text-secondary bg-bg';
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cls}`}>{cr.toFixed(2)}%</span>
  );
}
